import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PresetOut } from '@/api/types';
import { Button, Icon, Thumb } from '@/design-system';
import { presetQuery, useLibraryList } from '@/features/library/api';
import { cx } from '@/lib/cx';
import { formatNumber } from '@/lib/format';
import { pinOf } from './api';
import { useStructureLabels } from './labels';
import { PageStack, specOf } from './PageStack';
import type { StructureCtx } from './parts';
import { blankSection, MAX_PINS, type Sec } from './useStructure';
import st from './structure.module.css';
import s from './ai.module.css';

/** How much of the prompt the card shows (the rest goes to the variant agent in full, PR #47). */
const EXCERPT = 900;

/**
 * The library preset as the page's lead section: right after the header (first when there is none). A section of
 * that type already on the page moves there and takes the version as its primary design, keeping its other pins;
 * otherwise a new section is inserted.
 */
export function pinLead(secs: Sec[], preset: PresetOut): Sec[] | null {
  const ref = pinOf(preset);
  if (!ref) return null;
  const header = secs.findIndex((x) => x.section_type === 'header');
  const at = header >= 0 ? header + 1 : 0;
  const i = secs.findIndex((x) => x.section_type === preset.section_type);
  if (i >= 0) {
    const sec = secs[i] as Sec;
    const moved: Sec = { ...sec, presets: [ref, ...sec.presets.filter((p) => p.preset_version_id !== ref.preset_version_id)].slice(0, MAX_PINS) };
    const rest = secs.filter((_, j) => j !== i);
    const to = header >= 0 ? rest.findIndex((x) => x.section_type === 'header') + 1 : 0;
    return [...rest.slice(0, to), moved, ...rest.slice(to)];
  }
  return [...secs.slice(0, at), blankSection(preset.section_type, [ref]), ...secs.slice(at)];
}

/** Whether the structure already leads with this version (right after the header). */
export function leadsWith(secs: Sec[], preset: PresetOut | null): boolean {
  if (!preset?.latest_version_id) return false;
  const header = secs.findIndex((x) => x.section_type === 'header');
  return secs[header >= 0 ? header + 1 : 0]?.presets[0]?.preset_version_id === preset.latest_version_id;
}

export type PromptChoice = { preset: PresetOut | null; direction: string };

type Props = StructureCtx & {
  choice: PromptChoice;
  setChoice: (c: PromptChoice) => void;
  /** «Закрепить и сгенерировать»: pin the lead section, then generate with the direction */
  onGenerate: () => void;
  busy: boolean;
};

/**
 * «Из промпта библиотеки» (P6-X01, an additive 4th mode in AI mode's card language): the PM picks a library
 * preset — an authored or a catalog one, its prompt in full goes to the variant agent — which is pinned as the
 * page's lead section, and a short art direction («что взять из промпта», prefilled) that is sent as the
 * generation's `prompt`. It is what gave the owner's best results in one step.
 */
export function PromptMode({ doc, select, choice, setChoice, onGenerate, busy }: Props) {
  const { t } = useTranslation('structure');
  const { section, designName } = useStructureLabels();
  const [search, setSearch] = useState('');
  const list = useLibraryList({ search: search.trim() || undefined }, false);
  const chosen = choice.preset;
  const detail = useQuery({ ...presetQuery(chosen?.id ?? ''), enabled: !!chosen }).data;
  const version = detail?.versions.find((v) => v.id === chosen?.latest_version_id) ?? detail?.versions[0];
  const prompt = version?.prompt ?? null;
  const pinned = leadsWith(doc.secs, chosen);

  return (
    <div className={st.aiGrid}>
      <div className={st.aiMain}>
        <div className={s.card}>
          <div className={s.head}>
            <h2 className={s.title}>{t('prompt.title')}</h2>
            <span className={s.sub}>{t('prompt.sub')}</span>
          </div>
          <label className={s.searchBox}>
            <Icon name="search" size={15} />
            <input className={s.searchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('prompt.search')} aria-label={t('prompt.search')} />
          </label>
          <div role="radiogroup" aria-label={t('prompt.list_label')} className={s.pickList}>
            {list.items.length === 0 && !list.loading && <span className={s.sub}>{t('prompt.none')}</span>}
            {list.items.map((p) => {
              const pin = pinOf(p);
              const on = chosen?.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={!pin}
                  className={cx(s.pick, on && s.pickOn)}
                  onClick={() => setChoice({ ...choice, preset: p })}
                >
                  <span className={s.rowThumb}>
                    <Thumb {...specOf(pin)} className={s.fill} style={{ position: 'absolute' }} />
                  </span>
                  <span className={s.rowText}>
                    <span className={s.rowName}>{designName(p)}</span>
                    <span className={s.why}>{t('prompt.meta', { section: section(p.section_type), origin: t(`prompt.origin.${p.origin}`), version: p.version ?? '—' })}</span>
                  </span>
                  {on && <Icon name="check" size={15} className={s.pickCheck} />}
                </button>
              );
            })}
            {list.hasMore && (
              <button type="button" className={s.more} onClick={list.loadMore} aria-busy={list.loadingMore}>
                {t('prompt.more')}
              </button>
            )}
          </div>
          {chosen && (
            <div className={s.excerptBox}>
              <span className={st.caps}>{t('prompt.excerpt', { name: designName(chosen) })}</span>
              {prompt ? (
                <pre className={s.excerpt}>
                  {prompt.length > EXCERPT ? `${prompt.slice(0, EXCERPT)}…` : prompt}
                  {prompt.length > EXCERPT && <span className={s.excerptMore}>{`\n${t('prompt.excerpt_more', { count: prompt.length - EXCERPT, n: formatNumber(prompt.length - EXCERPT) })}`}</span>}
                </pre>
              ) : (
                <span className={s.sub}>{detail ? t('prompt.no_prompt') : t('prompt.loading')}</span>
              )}
            </div>
          )}
          <label className={s.directionLabel}>
            <span className={st.caps}>{t('prompt.direction')}</span>
            <textarea
              className={s.prompt}
              rows={4}
              value={choice.direction}
              maxLength={2000}
              onChange={(e) => setChoice({ ...choice, direction: e.target.value })}
              aria-label={t('prompt.direction')}
            />
          </label>
          <div className={s.runRow}>
            <span className={s.sub}>{chosen ? (pinned ? t('prompt.pinned', { section: section(chosen.section_type) }) : t('prompt.will_pin', { section: section(chosen.section_type) })) : t('prompt.pick_first')}</span>
            <Button icon="sparkles" iconSize={15} className={s.run} onClick={onGenerate} aria-busy={busy} aria-disabled={!chosen || undefined} data-action="prompt-run">
              {t('prompt.run')}
            </Button>
          </div>
        </div>
      </div>
      <div className={st.aiPreview}>
        <span className={st.caps}>{t('preview.title_short')}</span>
        <PageStack secs={chosen && !pinned ? (pinLead(doc.secs, chosen) ?? doc.secs) : doc.secs} onSelect={select} />
      </div>
    </div>
  );
}
